/**
 * Public Call Sheet — token-gated read-only view for external collaborators.
 *
 * Route: /share/call-sheet/:token
 *
 * No auth required. Server enforces token validity + published status and
 * returns the two-tab payload (roster, schedule blocks, set list). This view
 * mirrors the editor: Tab 1 "Callsheet" (roster + day schedule), Tab 2
 * "Set List". Mobile-first — collaborators read this on their phone.
 */
import { useEffect } from "react";
import { useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, MapPin, AlertCircle, CloudSun, Users, ListMusic, Clock } from "lucide-react";
import { format } from "date-fns";

interface PublicCallSheetData {
  callSheet: {
    id: string;
    title: string;
    eventDate: string | null;
    eventTime: string | null;
    loadInTime: string | null;
    runtimeOverrideMinutes: number | null;
    venueName: string | null;
    venueAddress: string | null;
    generalNotes: string | null;
    weatherCache: any;
    weatherCachedAt: string | null;
    status: string;
    publishedAt: string | null;
  };
  roster: Array<{ id: string; name: string; roleLabel: string | null; note: string | null; sortOrder: number }>;
  blocks: Array<{
    id: string;
    label: string;
    startTime: string;
    endTime: string | null;
    location: string | null;
    note: string | null;
    sortOrder: number;
    assignedRosterIds: string[];
  }>;
  setItems: Array<{ id: string; title: string; note: string | null; sortOrder: number }>;
  collaborator: { id: string; name: string; roles: string[] };
  runtimeDefaultMinutes: number | null;
}

function fmtDate(d: string | null): string {
  if (!d) return "TBD";
  try {
    return format(new Date(d), "EEE, MMM d, yyyy");
  } catch {
    return "TBD";
  }
}

function fmtClock(hhmm: string | null | undefined): string {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return "—";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function clockMins(hhmm: string | null | undefined): number {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return 0;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

export default function PublicCallSheetPage() {
  const [, params] = useRoute<{ token: string }>("/share/call-sheet/:token");
  const token = params?.token;

  const { data, isLoading, error } = useQuery<PublicCallSheetData>({
    queryKey: ["public-call-sheet", token],
    queryFn: async () => {
      const r = await fetch(`/api/public/call-sheet/${token}`, { credentials: "include" });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.message || `Status ${r.status}`);
      }
      return r.json();
    },
    enabled: !!token,
    retry: false,
  });

  useEffect(() => {
    if (data?.callSheet?.title) {
      document.title = `${data.callSheet.title} • Call Sheet`;
    }
  }, [data?.callSheet?.title]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              {(error as Error)?.message ||
                "This share link is invalid, has been revoked, or the call sheet hasn't been published yet."}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const cs = data.callSheet;
  const weather: any = cs.weatherCache;
  const rosterById = new Map(data.roster.map((r) => [r.id, r]));
  const roster = data.roster.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const blocks = data.blocks.slice().sort((a, b) => clockMins(a.startTime) - clockMins(b.startTime));
  const setItems = data.setItems.slice().sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-4">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-2">
              <CardTitle className="text-2xl break-words" data-testid="text-public-callsheet-title">
                {cs.title}
              </CardTitle>
              <CardDescription>
                {fmtDate(cs.eventDate)}
                {cs.eventTime ? ` • Event ${fmtClock(cs.eventTime)}` : ""}
                {cs.loadInTime ? ` • Load-in ${fmtClock(cs.loadInTime)}` : ""}
              </CardDescription>
              {cs.venueName && <p className="text-sm font-medium">{cs.venueName}</p>}
              {cs.venueAddress && (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(cs.venueAddress)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                  data-testid="link-public-venue-map"
                >
                  <MapPin className="h-3 w-3" />
                  {cs.venueAddress}
                </a>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Shared with <strong>{data.collaborator.name}</strong>
                {data.collaborator.roles?.length ? ` (${data.collaborator.roles.join(", ")})` : ""}
              </p>
            </div>
          </CardHeader>
          {cs.generalNotes && (
            <CardContent>
              <p className="text-sm whitespace-pre-wrap" data-testid="text-public-notes">
                {cs.generalNotes}
              </p>
            </CardContent>
          )}
        </Card>

        {/* Weather */}
        {weather?.current && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CloudSun className="h-4 w-4" />
                Weather
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <p>
                <span className="font-medium">{Math.round(weather.current.temperature_2m)}°F</span>
                {" • "}wind {Math.round(weather.current.wind_speed_10m)} mph
              </p>
            </CardContent>
          </Card>
        )}

        {/* Two-tab read view */}
        <Tabs defaultValue="callsheet" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="callsheet" data-testid="tab-public-callsheet">
              <Users className="h-4 w-4 mr-2" /> Callsheet
            </TabsTrigger>
            <TabsTrigger value="setlist" data-testid="tab-public-setlist">
              <ListMusic className="h-4 w-4 mr-2" /> Set List
            </TabsTrigger>
          </TabsList>

          <TabsContent value="callsheet" className="space-y-4 mt-4">
            {/* Roster */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Roster</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {roster.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="text-public-no-roster">
                    No roster listed.
                  </p>
                ) : (
                  roster.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-2 border rounded-lg p-2.5"
                      data-testid={`row-public-roster-${p.id}`}
                    >
                      <div className="min-w-0">
                        <span className="font-medium">{p.name}</span>
                        {p.note && <p className="text-xs text-muted-foreground">{p.note}</p>}
                      </div>
                      {p.roleLabel && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          {p.roleLabel}
                        </Badge>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Day schedule */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Day Schedule</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {blocks.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="text-public-no-blocks">
                    No schedule yet.
                  </p>
                ) : (
                  blocks.map((b) => {
                    const names = b.assignedRosterIds
                      .map((id) => rosterById.get(id)?.name)
                      .filter(Boolean);
                    return (
                      <div key={b.id} className="border rounded-lg p-3" data-testid={`row-public-block-${b.id}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-medium">{b.label}</span>
                          <span className="text-sm font-medium text-primary">
                            {fmtClock(b.startTime)}
                            {b.endTime ? `–${fmtClock(b.endTime)}` : ""}
                          </span>
                        </div>
                        {b.location && (
                          <a
                            href={`https://maps.google.com/?q=${encodeURIComponent(b.location)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                          >
                            <MapPin className="h-3 w-3" />
                            {b.location}
                          </a>
                        )}
                        {b.note && <p className="text-sm whitespace-pre-wrap mt-1">{b.note}</p>}
                        {names.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {names.map((n, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {n}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="setlist" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Set List</CardTitle>
                {(cs.runtimeOverrideMinutes ?? data.runtimeDefaultMinutes) != null && (
                  <CardDescription className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />{" "}
                    {cs.runtimeOverrideMinutes ?? data.runtimeDefaultMinutes} min total runtime
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-2">
                {setItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="text-public-no-setitems">
                    No set list yet.
                  </p>
                ) : (
                  setItems.map((item, idx) => (
                    <div
                      key={item.id}
                      className="flex items-start gap-2 border rounded-lg p-3"
                      data-testid={`row-public-setitem-${item.id}`}
                    >
                      <span className="text-sm text-muted-foreground w-5">{idx + 1}.</span>
                      <div className="min-w-0">
                        <p className="font-medium">{item.title}</p>
                        {item.note && <p className="text-xs text-muted-foreground mt-0.5">{item.note}</p>}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <p className="text-xs text-muted-foreground text-center pt-4">This call sheet is read-only.</p>
      </div>
    </div>
  );
}
